import { useState } from "react";
import { Lock, Monitor, Smartphone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { tr } from "../lib/i18n";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";

const THEMES = ["modern", "luxury", "classic", "bold"];
const name = (th) => tr(`x.theme_${th /* i18n-keys: modern,luxury,classic,bold */}`);

// Mini page that plays a short sample of the theme's motion on hover.
const Sample = ({ th }) => (
  <div className={`thx-sample thx-${th}`} aria-hidden="true">
    <div className="thx-hero" />
    <div className="thx-line l1" /><div className="thx-line l2" />
    <div className="thx-cards"><span /><span /><span /></div>
  </div>
);

function Card({ th, current, locked, onPreview, onApply, previewing }) {
  const isCur = th === current;
  return (
    <div className="thx-card" data-current={isCur ? "1" : "0"} data-testid={`theme-card-${th}`}>
      <Sample th={th} />
      <div className="p-4 grid gap-2">
        <div className="flex items-center justify-between gap-2">
          <strong>{name(th)}</strong>
          {isCur && <span className="text-xs font-semibold" style={{ color: "var(--ds-green-ink)" }} data-testid={`theme-current-${th}`}>{tr("x.theme_current")}</span>}
        </div>
        <div className="text-xs" style={{ color: "var(--ds-ink-muted)" }}>{tr(`x.theme_${th /* i18n-keys: modern,luxury,classic,bold */}_help`)}</div>
        <div className="flex gap-2 mt-1 flex-wrap">
          <button type="button" className="btn-secondary text-xs" aria-pressed={previewing} onClick={() => onPreview(th)} data-testid={`theme-preview-${th}`}>{tr("x.theme_preview")}</button>
          {!isCur && (locked
            ? <span className="text-xs self-center flex items-center gap-1" style={{ color: "var(--ds-ink-muted)" }} data-testid={`theme-locked-${th}`}><Lock size={12} /> {tr("lic.available_on", { plan: tr("lic.plan_growth") })}</span>
            : <button type="button" className="btn-primary text-xs" onClick={() => onApply(th)} data-testid={`theme-apply-${th}`}>{tr("x.theme_apply")}</button>)}
        </div>
      </div>
    </div>
  );
}

function Preview({ th, device, setDevice, onClose }) {
  return (
    <div className="panel p-4 grid gap-3" data-testid="theme-preview-frame">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <strong>{tr("thx.preview_mode")}: {name(th)}</strong>
        <div className="flex gap-2">
          <div className="seg" role="group">
            <button className={device === "desktop" ? "active" : ""} onClick={() => setDevice("desktop")} data-testid="theme-device-desktop"><Monitor size={14} /> {tr("thx.desktop")}</button>
            <button className={device === "phone" ? "active" : ""} onClick={() => setDevice("phone")} data-testid="theme-device-phone"><Smartphone size={14} /> {tr("thx.phone")}</button>
          </div>
          <button type="button" className="btn-ghost text-xs" onClick={onClose} data-testid="theme-preview-close">{tr("x.close")}</button>
        </div>
      </div>
      <div className={`thx-device ${device}`}>
        <AnimatePresence mode="wait">
          <motion.iframe key={`${th}-${device}`} title={tr("x.theme_preview")} src={`/?preview_theme=${th}`} className="thx-iframe"
            initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { duration: 0.25, ease: "easeOut" } }} exit={{ opacity: 0, transition: { duration: 0.15, ease: "easeIn" } }} />
        </AnimatePresence>
      </div>
    </div>
  );
}

// Preview choice survives a remount or reload of Settings (per browser tab).
function useKept(key, initial) {
  const [v, setV] = useState(() => { try { return sessionStorage.getItem(key) || initial; } catch { return initial; } });
  const set = (x) => { setV(x); try { if (x) sessionStorage.setItem(key, x); else sessionStorage.removeItem(key); } catch {} };
  return [v, set];
}

// Theme section: large preview cards, a device-framed live preview, and a confirmed Apply that saves straight away.
export const ThemePicker = ({ value, onApply, locked = false }) => {
  const [preview, setPreview] = useKept("thx_preview", null);
  const [device, setDevice] = useKept("thx_device", "desktop");
  const [ask, setAsk] = useState(null);
  const current = value || "modern";
  const apply = async () => {
    const th = ask;
    setAsk(null);
    try { await api.put("/settings", { public_theme: th }); onApply(th); toast.success(tr("thx.applied")); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="grid gap-4" data-testid="theme-picker">
      {locked && <p className="text-sm p-3 panel" data-testid="theme-locked-note">{tr("lic.theme_locked")}</p>}
      <p className="text-xs" style={{ color: "var(--ds-ink-muted)" }}>{tr("thx.hover_hint")}</p>
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        {THEMES.map((th) => <Card key={th} th={th} current={current} locked={locked} previewing={preview === th} onPreview={setPreview} onApply={setAsk} />)}
      </div>
      {preview && <Preview th={preview} device={device} setDevice={setDevice} onClose={() => setPreview(null)} />}
      <AlertDialog open={Boolean(ask)} onOpenChange={(o) => { if (!o) setAsk(null); }}>
        <AlertDialogContent data-testid="theme-apply-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{ask && tr("thx.confirm_title", { name: name(ask) })}</AlertDialogTitle>
            <AlertDialogDescription>{tr("thx.confirm_text")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="theme-apply-cancel">{tr("thx.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={apply} data-testid="theme-apply-confirm-btn">{tr("thx.apply")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
